import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { useMutation, useQuery } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";

import { PaymentsCreateOrderAttributes } from "~/api/Api";
import { ApiError } from "~/api/types.ts";
import { usePendingOrders } from "~/components/services/hooks/useOrdersWithPermissions.ts";
import { PendingOrder } from "~/components/services/orders/PendingOrder";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { stripePromise } from "~/helpers/load_stripe";
import { usePageTitle } from "~/hooks/usePageTitle";

import { apiClient } from "../../api/ApiClient";
import { NicheLinksPackage } from "../../components/services/nicheLinksPackage/NicheLinksPackage";
import Modal from "../../components/ui/modal/Modal";
import { PaginationConfig } from "../../components/ui/pagination/Pagination";
import Spinner from "../../components/ui/spinner/Spinner";
import { useStripeSession } from "../../hooks/useStripeSession";
import styles from "./ServicesListPage.module.scss";

interface SiteDomain {
  label: string;
  value: string;
}

const NicheLinksPage = () => {
  usePageTitle(PAGE_TITLES.authorityService);

  const [pagination, setPagination] = useState<PaginationConfig>({
    currentPage: 1,
    itemsPerPage: 10,
    nextPage: null,
    prevPage: null,
    totalPages: 1,
    totalItems: 10,
  });

  const [siteName, setSiteName] = useState<string>("alli");
  const {
    clientSecret,
    isModalOpen,
    isCreatingSession,
    openSession,
    closeModal,
  } = useStripeSession({
    sessionType: "pay",
    onSuccess: () => {},
    onError: () => {},
  });

  const { data: products, isLoading } = useQuery({
    queryKey: ["services", "products-list"],
    queryFn: async () => {
      const response = await apiClient.billing.productsList({
        "page[size]": 10,
        "page[number]": 1,
        s: "created_at ASC",
        q: {
          kind_eq: "product",
        },
      });

      return response.data?.["payments/products"];
    },
  });

  const product = products?.find((p) => p.name === "Niche Links Package");

  const { mutate: createOrder } = useMutation({
    mutationFn: async (order: PaymentsCreateOrderAttributes) => {
      const response = await apiClient.billing.ordersCreate(order);

      return response.data?.["payments/order"];
    },
    onSuccess: async () => {
      enqueueSnackbar("Order was successfully created", {
        variant: "success",
      });
      openSession();
    },
    onError: async (error: ApiError) => {
      const errorMessage =
        error?.error?.error?.message || "Something went wrong";
      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });

      if (error?.status === 403) {
        await refetchPendingOrders();
      }
    },
  });

  const { data: siteDomains, isLoading: isLoadingSiteDomains } = useQuery({
    queryKey: ["sites", pagination.currentPage, pagination.itemsPerPage],
    queryFn: async () => {
      const response = await apiClient.sites.sitesList({
        "page[size]": 1000,
        "page[number]": pagination.currentPage,
        s: "created_at ASC",
      });
      const { headers } = response;
      setPagination((prev: PaginationConfig) => ({
        ...prev,
        totalItems: Number(headers.get("x-pager-total-count")),
        nextPage: Number(headers.get("x-pager-next-page")),
        prevPage: Number(headers.get("x-pager-prev-page")),
        totalPages: Number(headers.get("x-pager-total-pages")),
      }));
      const siteDomains = response?.data?.sites?.map((site) => ({
        label: site.domain,
        value: site.id.toString(),
      }));

      return siteDomains;
    },
  });

  const { orders: pendingOrders, refetch: refetchPendingOrders } =
    usePendingOrders();
  const pendingOrder = pendingOrders[0];

  const handleCloseModal = () => {
    closeModal();
    refetchPendingOrders();
  };

  if (isLoading || !product) {
    return <Spinner />;
  }

  return (
    <>
      <div className={styles.page}>
        {pendingOrder && (
          <PendingOrder
            order={pendingOrder}
            refetchPendingOrders={refetchPendingOrders}
          />
        )}
        <h1 className={styles.title}>{PAGE_TITLES.authorityService}</h1>
        <NicheLinksPackage
          product={product}
          createOrder={createOrder}
          targetSites={siteDomains as SiteDomain[]}
          /** @todo the following props are never used. clean up when possible */
          // @ts-expect-error unused props
          isLoadingSiteDomains={isLoadingSiteDomains}
          pagination={pagination}
          setPagination={setPagination}
          siteName={siteName}
          setName={setSiteName}
        />
      </div>
      <Modal
        key="payOrder"
        isOpen={isModalOpen}
        closeModal={handleCloseModal}
        formId="cancelationForm"
        height="92%"
        width="80%"
        modalLabel={<h3></h3>}
        forceWhiteBackground
      >
        {isCreatingSession && <Spinner />}
        {clientSecret && stripePromise && (
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{ clientSecret }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        )}
      </Modal>
    </>
  );
};

export default NicheLinksPage;
